// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { Form, Input, Button, Select, Card, message, Divider, Avatar } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, TagOutlined } from '@ant-design/icons';
import { PageLayout, ActionButton } from '../../components/DesignSystem';
import { apiFetch } from '../../utils/apiFetch';

const { TextArea } = Input;
const { Option } = Select;

const CategoryEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        if (!token) { navigate('/login'); return; }
        const res = await apiFetch(`${API_URL}/categories/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        const img = data.image || '';
        form.setFieldsValue({
          name:        data.name        || '',
          description: data.description || '',
          status:      data.status      || 'Active',
          notes:       data.notes       || '',
          image:       img,
        });
        setImagePreview(img);
      } catch {
        message.error('Failed to load category');
        navigate('/categories/list');
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, [id, token]);

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { message.error('Image must be under 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target?.result as string;
      setImagePreview(dataUrl);
      form.setFieldValue('image', dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const finalImage = imagePreview || values.image || '';
      const payload = {
        name:        values.name?.trim(),
        description: values.description?.trim() || '',
        image:       finalImage,
        status:      values.status || 'Active',
        notes:       values.notes?.trim() || '',
      };
      const res = await apiFetch(`${API_URL}/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        message.success('✅ Category updated successfully');
        navigate(`/categories/view/${id}`);
      } else {
        const data = await res.json().catch(() => ({}));
        message.error(data.message || 'Failed to update category');
      }
    } catch {
      message.error('Network error occurred');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 50, textAlign: 'center' }}>⏳ Loading category...</div>;

  return (
    <PageLayout
      title="✏️ Edit Category"
      action={
        <div style={{ display: 'flex', gap: 10 }}>
          <ActionButton variant="add" label="Save Changes" onClick={() => form.submit()} />
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/categories/view/${id}`)}>Back</Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={() => message.error('Please fill in all required fields.')}
      >
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {imagePreview ? (
                <img src={imagePreview} alt="category" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
              ) : (
                <Avatar size={48} icon={<TagOutlined />} style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', fontSize: 20 }} />
              )}
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Category Details</div>
                <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 'normal' }}>Name, description and image</div>
              </div>
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          {/* Image upload */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 500, marginBottom: 8, color: '#374151', fontSize: 14 }}>Category Image</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {imagePreview ? (
                <img src={imagePreview} alt="preview" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
              ) : (
                <div style={{ width: 64, height: 64, borderRadius: 10, background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28 }}>🗂️</div>
              )}
              <div>
                <Button onClick={() => fileInputRef.current?.click()} style={{ marginBottom: 6, display: 'block' }}>
                  📁 Upload Image
                </Button>
                <div style={{ fontSize: 12, color: '#888' }}>JPG, PNG, GIF — or paste URL below</div>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFile} />
              </div>
            </div>
            <Form.Item name="image" style={{ marginTop: 12, marginBottom: 0 }}>
              <Input placeholder="https://example.com/image.png" onChange={e => setImagePreview(e.target.value)} />
            </Form.Item>
          </div>

          <Divider style={{ margin: '4px 0 20px' }} />

          <Form.Item name="name" label="Category Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Electronics, Office Supplies, Raw Materials" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Short description of this category" />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes (internal use)">
            <TextArea rows={3} placeholder="Internal notes about this category" />
          </Form.Item>
        </Card>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginBottom: 32 }}>
          <Button size="large" onClick={() => navigate(`/categories/view/${id}`)}>Cancel</Button>
          <Button
            type="primary" size="large" icon={<SaveOutlined />}
            onClick={() => form.submit()} loading={saving}
            style={{ background: '#667eea', borderColor: '#667eea', minWidth: 180 }}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </PageLayout>
  );
};

export default CategoryEdit;
